import z from 'zod';
import { filteredArray } from '@/lib/schema';
import type { JSRuntimeFS } from '@/lib/JSRuntime';
import type { AIProvider, AISettings, ProjectTemplate } from '@/contexts/AISettingsContext';
import type { GitSettings } from '@/contexts/GitSettingsContext';
import type { DeployProvider, DeploySettings } from '@/contexts/DeploySettingsContext';
import type { Integration, IntegrationsSettings } from '@/contexts/IntegrationsContext';

const CONFIG_DIR = '/config';
const AI_CONFIG_PATH = `${CONFIG_DIR}/ai.json`;
const GIT_CONFIG_PATH = `${CONFIG_DIR}/git.json`;
const DEPLOY_CONFIG_PATH = `${CONFIG_DIR}/deploy.json`;
const INTEGRATIONS_CONFIG_PATH = `${CONFIG_DIR}/integrations.json`;

const aiProviderSchema: z.ZodType<AIProvider> = z.object({
  id: z.string(),
  baseURL: z.string().url(),
  apiKey: z.string().optional(),
  nostr: z.boolean().optional(),
  proxy: z.boolean().optional(),
});

const projectTemplateSchema: z.ZodType<ProjectTemplate> = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  url: z.string().url(),
});

const providerModelSchema = z.string().regex(
  /^[^/]+\/.+$/,
  'Must be in format provider/model (e.g., "openai/gpt-4o")',
);

const aiSettingsSchema = z.object({
  providers: filteredArray(aiProviderSchema),
  recentlyUsedModels: filteredArray(providerModelSchema),
  projectTemplates: filteredArray(projectTemplateSchema).optional(),
});

const gitCredentialSchema = z.object({
  username: z.string(),
  password: z.string(),
});

const gitHostTokenSchema = z.object({
  token: z.string(),
  username: z.string().optional(),
  scopes: z.array(z.string()).optional(),
  createdAt: z.number().optional(),
});

const gitSettingsSchema = z.object({
  credentials: z.record(z.string(), gitCredentialSchema),
  hostTokens: z.record(z.string(), gitHostTokenSchema).optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  coAuthorEnabled: z.boolean().optional(),
});

const baseDeployProviderSchema = z.object({
  id: z.string(),
  name: z.string(),
  proxy: z.boolean().optional(),
});

const shakespeareDeployProviderSchema = baseDeployProviderSchema.extend({
  type: z.literal('shakespeare'),
  host: z.string().optional(),
});

const netlifyProviderSchema = baseDeployProviderSchema.extend({
  type: z.literal('netlify'),
  apiKey: z.string(),
  baseURL: z.string().optional(),
});

const vercelProviderSchema = baseDeployProviderSchema.extend({
  type: z.literal('vercel'),
  apiKey: z.string(),
  baseURL: z.string().optional(),
});

const nsiteProviderSchema = baseDeployProviderSchema.extend({
  type: z.literal('nsite'),
  gateway: z.string(),
  relayUrls: z.array(z.string()),
  blossomServers: z.array(z.string()),
});

const deployProviderSchema: z.ZodType<DeployProvider> = z.discriminatedUnion('type', [
  shakespeareDeployProviderSchema,
  netlifyProviderSchema,
  vercelProviderSchema,
  nsiteProviderSchema,
]);

const deploySettingsSchema = z.object({
  providers: filteredArray(deployProviderSchema),
});

const baseIntegrationSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  enabled: z.boolean(),
  createdAt: z.number(),
  updatedAt: z.number(),
});

const stripeIntegrationSchema = baseIntegrationSchema.extend({
  type: z.literal('stripe'),
  accessToken: z.string(),
  stripeAccountId: z.string(),
  publishableKey: z.string(),
  livemode: z.boolean(),
  scopes: z.array(z.string()).optional(),
  refreshToken: z.string().optional(),
  metadata: z
    .object({
      stripeUserId: z.string().optional(),
      businessName: z.string().optional(),
      email: z.string().optional(),
      country: z.string().optional(),
    })
    .passthrough()
    .optional(),
});

const supabaseIntegrationSchema = baseIntegrationSchema.extend({
  type: z.literal('supabase'),
  projectUrl: z.string().url(),
  anonKey: z.string(),
  serviceRoleKey: z.string().optional(),
  projectRef: z.string(),
  metadata: z
    .object({
      projectName: z.string().optional(),
      region: z.string().optional(),
    })
    .passthrough()
    .optional(),
});

const integrationSchema: z.ZodType<Integration> = z.discriminatedUnion('type', [
  stripeIntegrationSchema,
  supabaseIntegrationSchema,
]);

const integrationsSettingsSchema = z.object({
  integrations: filteredArray(integrationSchema),
  projectIntegrations: z.record(z.string(), z.array(z.string())).optional(),
});

/**
 * Ensures the config directory exists
 */
async function ensureConfigDir(fs: JSRuntimeFS): Promise<void> {
  try {
    await fs.stat(CONFIG_DIR);
  } catch {
    await fs.mkdir(CONFIG_DIR, { recursive: true });
  }
}

/**
 * Read AI settings from VFS
 */
export async function readAISettings(fs: JSRuntimeFS): Promise<AISettings> {
  const defaultSettings: AISettings = {
    providers: [],
    recentlyUsedModels: [],
    projectTemplates: [],
  };

  try {
    const content = await fs.readFile(AI_CONFIG_PATH, 'utf8');
    const data = JSON.parse(content);
    const parsed = aiSettingsSchema.parse(data);
    return {
      ...parsed,
      projectTemplates: parsed.projectTemplates || [],
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('AI settings parsing error:', error.errors);
    }
    return defaultSettings;
  }
}

/**
 * Write AI settings to VFS
 */
export async function writeAISettings(fs: JSRuntimeFS, settings: AISettings): Promise<void> {
  await ensureConfigDir(fs);
  await fs.writeFile(AI_CONFIG_PATH, JSON.stringify(settings, null, 2), 'utf8');
}

/**
 * Read Git settings from VFS
 */
export async function readGitSettings(fs: JSRuntimeFS): Promise<GitSettings> {
  const defaultSettings: GitSettings = {
    credentials: {},
    hostTokens: {},
    coAuthorEnabled: true,
  };

  // Try to read from VFS first
  try {
    const content = await fs.readFile(GIT_CONFIG_PATH, 'utf8');
    const data = JSON.parse(content);
    const parsed = gitSettingsSchema.parse(data);
    // Ensure hostTokens exists even if not in parsed data
    return {
      ...parsed,
      hostTokens: parsed.hostTokens || {},
    };
  } catch {
    return defaultSettings;
  }
}

/**
 * Write Git settings to VFS
 */
export async function writeGitSettings(fs: JSRuntimeFS, settings: GitSettings): Promise<void> {
  await ensureConfigDir(fs);
  await fs.writeFile(GIT_CONFIG_PATH, JSON.stringify(settings, null, 2), 'utf8');
}

/**
 * Read Deploy settings from VFS
 */
export async function readDeploySettings(fs: JSRuntimeFS): Promise<DeploySettings> {
  const defaultSettings: DeploySettings = {
    providers: [],
  };

  try {
    const content = await fs.readFile(DEPLOY_CONFIG_PATH, 'utf8');
    const data = JSON.parse(content);
    return deploySettingsSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('Deploy settings parsing error:', error.errors);
    }
    return defaultSettings;
  }
}

/**
 * Write Deploy settings to VFS
 */
export async function writeDeploySettings(fs: JSRuntimeFS, settings: DeploySettings): Promise<void> {
  await ensureConfigDir(fs);
  await fs.writeFile(DEPLOY_CONFIG_PATH, JSON.stringify(settings, null, 2), 'utf8');
}

/**
 * Read Integrations settings from VFS
 */
export async function readIntegrationsSettings(fs: JSRuntimeFS): Promise<IntegrationsSettings> {
  const defaultSettings: IntegrationsSettings = {
    integrations: [],
    projectIntegrations: {},
  };

  try {
    const content = await fs.readFile(INTEGRATIONS_CONFIG_PATH, 'utf8');
    const data = JSON.parse(content);
    const parsed = integrationsSettingsSchema.parse(data);
    return {
      ...parsed,
      projectIntegrations: parsed.projectIntegrations || {},
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('Integrations settings parsing error:', error.errors);
    }
    return defaultSettings;
  }
}

/**
 * Write Integrations settings to VFS
 */
export async function writeIntegrationsSettings(
  fs: JSRuntimeFS,
  settings: IntegrationsSettings
): Promise<void> {
  await ensureConfigDir(fs);
  await fs.writeFile(INTEGRATIONS_CONFIG_PATH, JSON.stringify(settings, null, 2), 'utf8');
}

